import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import {PipelineConfigJsonSpec} from 'pipelines/StepShapeUtils';
import DataTestAttrs from 'test/lib/cypress/shared/DataTestAttrs';
import makeUseGlobalClientState from 'ui-library/hooks/global-client-state/makeUseGlobalClientState';
import {TabContainer, Tab} from 'ui-library/TabContainer';
import {JSONValue} from 'util/Types';

import {TabbedUISchema} from '../step-config-settings/StepConfigUISchemas';
import {TAB_ORDER_UI_SCHEMA_KEY, ConfigShapePath} from '../StepConfigEditorTypes';
import StepConfigEditorRouterV2 from './StepConfigEditorRouterV2';

export interface StepConfigFormTabContainerProps {
  clientStateId: string;
  uiSchema: TabbedUISchema;
  config: Record<string, JSONValue>;
  onChangeConfig: (config: JSONValue, path: ConfigShapePath, newValue: JSONValue) => void;
  configShape: PipelineConfigJsonSpec;
  dataTestAttrs?: Pick<DataTestAttrs, 'key' | 'index'>;
  disabled?: boolean;
}

const tabContainerState = makeUseGlobalClientState<string | null>(null);

export default function StepConfigFormTabContainer({
  clientStateId,
  uiSchema,
  config,
  onChangeConfig,
  configShape,
  dataTestAttrs,
  disabled,
}: StepConfigFormTabContainerProps): JSX.Element {
  const [maybeActiveTab, setActiveTab] = tabContainerState.useGlobalClientState(clientStateId);

  // Resolve tab order - support both static arrays and dynamic functions
  const tabOrder =
    typeof uiSchema[TAB_ORDER_UI_SCHEMA_KEY] === 'function'
      ? uiSchema[TAB_ORDER_UI_SCHEMA_KEY](config)
      : uiSchema[TAB_ORDER_UI_SCHEMA_KEY];

  const activeTab = maybeActiveTab ?? tabOrder[0];

  return (
    <TabContainer
      activeTab={activeTab}
      onRequestTabChange={setActiveTab}
      paneClassName={css(styles.tabContainerPane)}
      centerNavItem={true}
    >
      {tabOrder.map((tabKey: string) => (
        <Tab key={tabKey} tabKey={tabKey} title={uiSchema[tabKey].displayName}>
          <StepConfigEditorRouterV2
            uiSchema={uiSchema[tabKey].uiSchema}
            config={config}
            onChangeConfig={onChangeConfig}
            configShape={configShape}
            dataTestAttrs={dataTestAttrs}
            disabled={disabled}
          />
        </Tab>
      ))}
    </TabContainer>
  );
}

const styles = StyleSheet.create({
  tabContainerPane: {
    marginTop: 15,
  },
});
